import React from 'react';
import ReactModal from 'react-modal';
import { X } from 'lucide-react';
import { BtnDefault } from './Buttons';
import { useThemeStore } from '../../state/theme';

interface ModalProps extends ReactModal.Props {
  children: React.ReactNode;
  title: string;
  contentClassName?: string;
  width?: string | number;
  height?: string | number;
  minHeight?: string | number;
  footer?: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({
  children,
  title,
  contentClassName = '',
  width = '90%',
  minHeight = '20%',
  height,
  footer,
  onRequestClose,
  isOpen,
  ...rest
}) => {
  const { isDarkMode } = useThemeStore();

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
      ariaHideApp={false}
      style={{
        overlay: {
          backgroundColor: isDarkMode
            ? 'rgba(0, 0, 0, 0.75)'
            : 'rgba(0, 0, 0, 0.25)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        content: {
          backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
          color: isDarkMode ? '#ffffff' : '#000000',
          width,
          minHeight,
          maxHeight: '90vh',
          height: height || 'auto',
          borderRadius: '8px',
          padding: '1.25rem',
          inset: 'unset',
          overflow: 'auto',
        },
      }}
      {...rest}
    >
      <div
        className={`flex flex-col gap-4 ${isDarkMode ? 'text-white' : 'text-black'} ${contentClassName}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <BtnDefault onClick={onRequestClose} aria-label="Close modal">
            <X size={16} />
          </BtnDefault>
        </div>

        {/* Content */}
        <div className="flex-1">{children}</div>

        {/* Footer (optional) */}
        {footer && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            {footer}
          </div>
        )}
      </div>
    </ReactModal>
  );
};

export default Modal;
